import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { workshopDashboardService } from '@/app/services/dashboardService';
import { Car, Wrench, CheckCircle, Truck } from 'lucide-react';

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value }) => (
  <Card className="flex-1 bg-white dark:bg-card">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-slate-100 dark:bg-slate-800">
          <Icon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{label}</p>
          <p className="text-2xl font-medium text-slate-800 dark:text-slate-200">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatCardSkeleton: React.FC = () => (
  <Card className="flex-1">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
        <div className="min-w-0 flex-1">
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-7 w-12" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const TodayActivityStrip: React.FC = () => {
  const { data: todayStats, isLoading } = useQuery({
    queryKey: ['workshopDashboard', 'todayStats'],
    queryFn: () => workshopDashboardService.getTodayStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 15000,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col sm:flex-row gap-3">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
    );
  }

  const stats = [
    { icon: Car, label: 'New Arrivals', value: todayStats?.newArrivals || 0 },
    { icon: Wrench, label: 'In Progress', value: todayStats?.inProgress || 0 },
    { icon: CheckCircle, label: 'Ready to Deliver', value: todayStats?.readyForDelivery || 0 },
    { icon: Truck, label: 'Delivered Today', value: todayStats?.deliveredToday || 0 },
  ];

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};

export default TodayActivityStrip;
